import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from "@mui/material";
import {
  Check as CheckIcon,
  Close as CloseIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as SuperAdminIcon,
  Person as UserIcon,
  Store as MerchantIcon,
  Engineering as OperatorIcon,
} from "@mui/icons-material";
import { useRBAC } from "../../hooks/useRBAC";
import { useAuthStore } from "../../stores/authStore";

const RBACDemoPage: React.FC = () => {
  const { user } = useAuthStore();
  const {
    userRole,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    hasAnyRole,
    hasAllRoles,
  } = useRBAC();

  const modules = [
    "dashboard",
    "users",
    "cardholders",
    "transactions",
    "branches",
    "sales_points",
    "categories",
    "products",
    "inventory",
    "sales",
    "purchases",
    "recharges",
    "reports",
  ];

  const getRoleIcon = (role: string) => {
    switch (role) {
      case "SUPER_ADMIN":
      case "superadmin":
        return <SuperAdminIcon />;
      case "ADMIN":
      case "admin":
        return <AdminIcon />;
      case "OPERATOR":
      case "operator":
        return <OperatorIcon />;
      case "MERCHANT":
      case "merchant":
        return <MerchantIcon />;
      default:
        return <UserIcon />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case "SUPER_ADMIN":
      case "superadmin":
        return "error";
      case "ADMIN":
      case "admin":
        return "warning";
      case "OPERATOR":
      case "operator":
        return "info";
      case "MERCHANT":
      case "merchant":
        return "success";
      default:
        return "default";
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Role-Based Access Control Demo
      </Typography>

      <Grid container spacing={3}>
        {/* Current User Info */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Current User
              </Typography>
              {user ? (
                <>
                  <Typography variant="body1">
                    <strong>Name:</strong> {user.name}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Email:</strong> {user.email}
                  </Typography>
                  <Box mt={2}>
                    <Chip
                      icon={getRoleIcon(user.role)}
                      label={user.role}
                      color={getRoleColor(user.role) as any}
                      variant="outlined"
                    />
                  </Box>
                </>
              ) : (
                <Alert severity="warning">No user logged in</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Role Checks */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Role Checks
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    {hasAnyRole("SUPER_ADMIN") ? (
                      <CheckIcon color="success" />
                    ) : (
                      <CloseIcon color="error" />
                    )}
                  </ListItemIcon>
                  <ListItemText primary="Is Super Admin" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {hasAnyRole("ADMIN") ? (
                      <CheckIcon color="success" />
                    ) : (
                      <CloseIcon color="error" />
                    )}
                  </ListItemIcon>
                  <ListItemText primary="Is Admin" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {hasAnyRole("OPERATOR") ? (
                      <CheckIcon color="success" />
                    ) : (
                      <CloseIcon color="error" />
                    )}
                  </ListItemIcon>
                  <ListItemText primary="Is Operator" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {hasAnyRole("MERCHANT") ? (
                      <CheckIcon color="success" />
                    ) : (
                      <CloseIcon color="error" />
                    )}
                  </ListItemIcon>
                  <ListItemText primary="Is Merchant" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                <Button
                  variant="outlined"
                  disabled={!canCreate("users")}
                  startIcon={canCreate("users") ? <CheckIcon /> : <CloseIcon />}
                >
                  Create User
                </Button>
                <Button
                  variant="outlined"
                  disabled={!canUpdate("users")}
                  startIcon={canUpdate("users") ? <CheckIcon /> : <CloseIcon />}
                >
                  Edit User
                </Button>
                <Button
                  variant="outlined"
                  disabled={!canDelete("users")}
                  startIcon={canDelete("users") ? <CheckIcon /> : <CloseIcon />}
                >
                  Delete User
                </Button>
                <Button
                  variant="outlined"
                  disabled={!canCreate("recharges")}
                  startIcon={
                    canCreate("recharges") ? <CheckIcon /> : <CloseIcon />
                  }
                >
                  Create Recharge
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Module Permissions */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Module Permissions
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Green checkmarks indicate you have access to perform the action on
            the module
          </Typography>

          <Box mt={2}>
            {modules.map((module) => (
              <Box key={module} mb={2}>
                <Typography
                  variant="subtitle1"
                  sx={{ textTransform: "capitalize", mb: 1 }}
                >
                  <strong>{module.replace(/([A-Z])/g, " $1").trim()}</strong>
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Chip
                    icon={
                      canRead(module as any) ? <CheckIcon /> : <CloseIcon />
                    }
                    label="Read"
                    color={canRead(module as any) ? "success" : "error"}
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    icon={
                      canCreate(module as any) ? <CheckIcon /> : <CloseIcon />
                    }
                    label="Create"
                    color={canCreate(module as any) ? "success" : "error"}
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    icon={
                      canUpdate(module as any) ? <CheckIcon /> : <CloseIcon />
                    }
                    label="Update"
                    color={canUpdate(module as any) ? "success" : "error"}
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    icon={
                      canDelete(module as any) ? <CheckIcon /> : <CloseIcon />
                    }
                    label="Delete"
                    color={canDelete(module as any) ? "success" : "error"}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Permission Logic Explanation */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            How It Works
          </Typography>
          <Typography variant="body2" paragraph>
            This system implements role-based access control (RBAC) where:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="SUPER_ADMIN has full access to all modules and actions"
                secondary="Can read, create, update, and delete anything"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="ADMIN has access to most modules with full CRUD permissions"
                secondary="Can manage users, branches, sales points, etc."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="OPERATOR has limited access focused on operations"
                secondary="Can manage cardholders, transactions, recharges, etc."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="MERCHANT has access to sales and inventory related modules"
                secondary="Can manage products, categories, sales, purchases, etc."
              />
            </ListItem>
          </List>
          <Alert severity="info" sx={{ mt: 2 }}>
            Your current role is: <strong>{user?.role || "Unknown"}</strong>.
            The permissions shown above are based on this role.
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RBACDemoPage;
